import { DeckKey, TarotCardData } from '../types/tarot';

export interface InterpretationUsage {
  usedToday: number;
  dailyLimit: number;
  remaining: number;
  priceUsd?: number;
  priceWei?: string | null;
}

export interface InterpretationResponse {
  success?: boolean;
  interpretation: string;
  usage: InterpretationUsage;
  meta?: {
    model?: string | null;
    tokensUsed?: number | null;
    recordId?: string | null;
  };
}

interface InterpretationRequest {
  fid: number;
  cards: TarotCardData[];
  deckKey: DeckKey;
  question?: string;
}

export class InterpretationError extends Error {
  status?: number;
  usage?: InterpretationUsage;

  constructor(message: string, status?: number, usage?: InterpretationUsage) {
    super(message);
    this.name = 'InterpretationError';
    this.status = status;
    this.usage = usage;
  }
}

const parseErrorResponse = async (response: Response) => {
  try {
    const data = await response.json();
    return data;
  } catch (error) {
    return null;
  }
};

export const requestInterpretation = async ({ fid, cards, deckKey, question }: InterpretationRequest): Promise<InterpretationResponse> => {
  if (import.meta.env.VITE_MOCK_INTERPRETATION === 'true') {
    console.log('✨ Mock interpretation enabled, returning canned response');
    return {
      success: true,
      interpretation:
        'Mock insight: You are in a safe testing universe. Trust your instincts, keep iterating, and remember that reversed energies simply mean “check the console.”',
      usage: {
        usedToday: 1,
        dailyLimit: 10,
        remaining: 9,
        priceUsd: 0,
        priceWei: '0',
      },
      meta: {
        model: 'mock',
        tokensUsed: 0,
        recordId: null,
      },
    };
  }
  const payload = {
    fid,
    cards,
    deckKey,
    question,
  };

  const response = await fetch('/api/interpretation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await parseErrorResponse(response);
    const message = errorBody?.error || response.statusText || 'Failed to generate interpretation';
    throw new InterpretationError(message, response.status, errorBody?.usage);
  }

  const data = (await response.json()) as InterpretationResponse;
  return data;
};

export const fetchInterpretationUsage = async (fid: number): Promise<InterpretationUsage> => {
  if (import.meta.env.VITE_MOCK_INTERPRETATION === 'true') {
    console.log('✨ Mock interpretation enabled, returning canned usage');
    return {
      usedToday: 1,
      dailyLimit: 10,
      remaining: 9,
      priceUsd: 0,
      priceWei: '0',
    };
  }
  const params = new URLSearchParams({ fid: fid.toString(), _t: Date.now().toString() });
  const response = await fetch(`/api/interpretation?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorBody = await parseErrorResponse(response);
    const message = errorBody?.error || response.statusText || 'Failed to load interpretation usage';
    throw new InterpretationError(message, response.status, errorBody?.usage);
  }

  const data = await response.json();
  return data.usage as InterpretationUsage;
};
